import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import useCrmObjectTypeListingFilters from 'framework-listing-lib/hooks/useCrmObjectTypeListingFilters';
import useHandleQueryParams from 'framework-listing-lib/internal/deepLink/hooks/useHandleQueryParams';
import { useCurrentUIMode, useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import { Metrics } from 'framework-listing-lib/internal/metrics';
import { useListingLibData } from 'framework-listing-lib/internal/providers/DataProvider';
import { useHasViews, useOnViewIdChange, useViewPropsCurrentViewId } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import { getViewId } from 'framework-listing-lib/internal/ViewTabs/utils/views';
import { useCurrentViewContext } from 'framework-listing-lib/internal/ViewTabs/providers/CurrentViewProvider';
import { DEFAULT_VIEW_ID_TO_ID_LABEL } from 'framework-listing-lib/internal/ViewTabs/constants/views';
import { jsx as _jsx } from "react/jsx-runtime";
const ViewsContext = /*#__PURE__*/createContext(undefined);
export default function ViewsProvider({
  children,
  onChangeUIMode
}) {
  const currentUIMode = useCurrentUIMode();
  const isFoldersMode = useIsFoldersMode();
  const urlParams = useHandleQueryParams();
  const hasViews = useHasViews();
  const {
    isFilterFromCustomView
  } = useCrmObjectTypeListingFilters();
  const onViewIdChange = useOnViewIdChange();
  const currentViewIdProp = useViewPropsCurrentViewId();
  const {
    currentView,
    loading: loadingCurrentView
  } = useCurrentViewContext();

  /**
   * Global column state.
   * Reads from @columnKeys prop sent to CrmListingTable
   */
  const [columns, setColumns] = useState([]);
  const [numberOfFrozenColumns, setNumberOfFrozenColumns] = useState(undefined);
  const [cardProperties, setCardProperties] = useState([]);
  const [quickFilterPropertyNames, setQuickFilterPropertyNames] = useState([]);
  const [viewUpdateState, setViewUpdateState] = useState({
    changedCardProperties: false,
    changedColumns: false,
    changedQuickFilterPropertyNames: false,
    changedSort: false,
    changedUIMode: false,
    mLangGroup: {
      changed: false,
      showAll: false
    }
  });
  const {
    loading: loadingListingLibData
  } = useListingLibData();
  const hasChangedFilters = hasViews && !isFilterFromCustomView;
  const value = useMemo(() => {
    return {
      cardProperties,
      currentView,
      columns,
      isCurrentViewModified: hasChangedFilters || viewUpdateState.changedColumns || viewUpdateState.changedSort || viewUpdateState.changedUIMode || viewUpdateState.mLangGroup.changed || viewUpdateState.changedCardProperties || viewUpdateState.changedQuickFilterPropertyNames,
      isLoading: !isFoldersMode && hasViews && (
      // view is only ready after we set columns in the useEffect below
      loadingCurrentView || columns.length === 0),
      numberOfFrozenColumns,
      quickFilterPropertyNames,
      setCardProperties,
      setColumns,
      setNumberOfFrozenColumns,
      setQuickFilterPropertyNames,
      setViewUpdateState,
      viewUpdateState
    };
  }, [cardProperties, columns, currentView, hasViews, hasChangedFilters, isFoldersMode, loadingCurrentView, numberOfFrozenColumns, quickFilterPropertyNames, viewUpdateState]);

  /**
   * Update draft view state when:
   *  1 - All views have been fetched
   *  2 - Current view changes
   *
   * This is done outside of a useEffect as an SLA optimization.
   * Columns being set blocks the initial load, and needs to be done ASAP.
   * If quick-fetched, the view could theoretically be available on the first render,
   * so there's no need to wait for the next useEffect cycle
   */
  const [prevViewId, setPrevViewId] = useState(undefined);
  if (currentView && prevViewId !== currentView.id && !loadingCurrentView && !loadingListingLibData && hasViews) {
    var _currentView$quickFil, _currentView$quickFil2;
    setPrevViewId(currentView.id);
    setColumns(currentView.columns.map(column => column.name));
    setQuickFilterPropertyNames((_currentView$quickFil = (_currentView$quickFil2 = currentView.quickFilterProperties) === null || _currentView$quickFil2 === void 0 ? void 0 : _currentView$quickFil2.map(({
      name
    }) => name)) !== null && _currentView$quickFil !== void 0 ? _currentView$quickFil : []);
    setNumberOfFrozenColumns(currentView.state.numberOfFrozenColumns || 0);
    setCardProperties(currentView.state.cardProperties || []);
    setViewUpdateState({
      changedCardProperties: false,
      changedColumns: false,
      changedQuickFilterPropertyNames: false,
      changedSort: false,
      changedUIMode: Boolean(currentView.state.uiMode && currentUIMode !== currentView.state.uiMode),
      mLangGroup: {
        changed: false,
        showAll: currentView.state && currentView.state.mLangGroup ? currentView.state.mLangGroup.showAll : false
      }
    });
  }

  /**
   * Call @onChangeUIMode when switching to a view which has a different UIMode
   * than the current UI mode being shown.
   * This must be done in an effect as it likely updates state outside of the current component
   */
  const [prevViewIdForEffect, setPrevViewIdForEffect] = useState(undefined);
  useEffect(() => {
    if (!hasViews || loadingListingLibData || loadingCurrentView || !currentView) {
      return;
    }
    if (currentView.id === prevViewIdForEffect) {
      return;
    }
    setPrevViewIdForEffect(currentView.id);
    if (currentView.state.uiMode && currentUIMode !== currentView.state.uiMode && onChangeUIMode) {
      onChangeUIMode(currentView.state.uiMode);
    }
  }, [currentUIMode, currentView, hasViews, loadingCurrentView, loadingListingLibData, onChangeUIMode, prevViewIdForEffect]);

  /**
   * Update columns from query string
   */
  const [hasUsedQueryParams, setHasUsedQueryParams] = useState(false);
  useEffect(() => {
    if (loadingListingLibData || loadingCurrentView || !urlParams || hasUsedQueryParams) {
      return;
    }
    if (!urlParams.columns.length) {
      return;
    }
    setColumns(urlParams.columns);
    setViewUpdateState(prev => Object.assign({}, prev, {
      changedColumns: true
    }));
    setHasUsedQueryParams(true);
  }, [hasUsedQueryParams, loadingListingLibData, loadingCurrentView, urlParams]);

  /**
   * Update the consumer's viewId state/prop when
   * the current view doesn't match it.
   * this can happen if:
   *  - the user doesn't have permission to see the requested view,
   *  - the requested view was deleted
   *  - no view was requested
   *
   * This relies on @currentView updating in response to the currentViewId prop **before** this useEffect runs,
   * otherwise it will just overwrite any changes the consumer tries to make to currentViewId
   */
  useEffect(() => {
    if (isFoldersMode || loadingListingLibData || loadingCurrentView || !currentView || !hasViews) {
      return;
    }

    //When changing to a different view on initial page load, quick fetch
    //potentially caches data for the incorrect view. This will check
    //if this is the case and remove the cache
    const isSameViewId = currentViewIdProp === currentView.id;
    const isSameViewIdLabel = currentView.idLabel && currentViewIdProp === currentView.idLabel;
    const matchesLegacyDefaultViewId = Boolean(currentViewIdProp && DEFAULT_VIEW_ID_TO_ID_LABEL[currentViewIdProp] && (DEFAULT_VIEW_ID_TO_ID_LABEL[currentViewIdProp] === currentView.id || DEFAULT_VIEW_ID_TO_ID_LABEL[currentViewIdProp] === currentView.idLabel));
    const shouldRedirect = !isSameViewId && !isSameViewIdLabel && !matchesLegacyDefaultViewId;
    if (shouldRedirect) {
      // Historical metric naming from when we used to remove the crm-search quick-fetch in this case
      // Doing so doesn't work because removing a quick-fetch in progress just causes the whenFinished/onError promise wrapper to hang
      Metrics.counter('ignore-quick-fetch-cache').increment();
      onViewIdChange(getViewId(currentView), {
        redirect: true
      });
    }
  }, [currentView, currentViewIdProp, hasViews, isFoldersMode, loadingListingLibData, loadingCurrentView, onViewIdChange]);
  return /*#__PURE__*/_jsx(ViewsContext.Provider, {
    value: value,
    children: children
  });
}
export function useViews() {
  const context = useContext(ViewsContext);
  if (context === undefined) {
    throw new Error('useViews must be used within a ViewsProvider');
  }
  return context;
}